import React, { useEffect, useState } from "react";
import PrimarySideBar from "../../components/common/sideBar/primarySideBar";
import InputIterator from '../../components/common/inputIterator';
import AddButtonSection from "../../components/common/AddButtonSection";
import { DevTool } from "@hookform/devtools";
import { useForm } from "react-hook-form";
import { addVisitsForPatient } from "../../utils/serviceData";
import { commonGetApi, commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import Loader from "../../components/common/loader";
import { dateFormat } from "../../utils/utils";
import { showToast } from "../../components/common/toast";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";

export default function AddVisitsForPatient({ patientId, ...props }) {
    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
        watch,
        reset,
    } = useForm({
        shouldUnregister: false // Preserve values even when field is hidden
    });
    const [visible, setVisible] = useState(false);
    const [renderInput, setRenderInput] = useState({ formRender: addVisitsForPatient });
    const [patientVisitAllDetails, setPatientVisitAllDetails] = useState(null);
    const [purposes, setPurposes] = useState(null);
    const selectedPurpose = watch("purpose");
    const [serverLoading, setServerLoading] = useState(null);
    console.log(patientVisitAllDetails);

    const init = async () => {
        try {
            const data = await commonGetApi(API_ENDPOINTS.GET_EXAMINER_DETAILS);
            const user = data?.data?.data;
            setRenderInput((prev) => ({
                ...prev,
                formRender: prev?.formRender?.map((field) => {
                    if (field?.name === "examined_by") {
                        return {
                            ...field,
                            options: user?.map((user) => ({
                                label: user?.user_name,
                                value: user?.user_id,
                            })),
                        };
                    }
                    return field;
                }),
                formName: "editProfile",
                buttons: addVisitsButton,
                header: <div className="sideBar-header">Add Visit</div>,
            }));

            const visitDetails = {
                searchValue: "",
                patient_id: patientId
            };
            const patientVisit = await commonPostApi(
                API_ENDPOINTS.GET_ALL_PATIENT_VISIT_DETAILS,
                visitDetails
            );

            setPatientVisitAllDetails(patientVisit?.data?.data);

            const lookupData = {
                lookup_type_name: "VISIT_PURPOSE",
            };
            const purposeDetails = await commonPostApi(
                API_ENDPOINTS.SEARCH_LOOKUP_DETAILS,
                lookupData
            );
            setRenderInput((prev) => ({
                ...prev,
                formRender: prev?.formRender?.map((field) => {
                    if (field?.name === "purpose") {
                        return {
                            ...field,
                            options: purposeDetails?.data?.data?.map((purpose) => ({
                                label: purpose?.name,
                                value: purpose?.lookup_id,
                            })),
                        };
                    }
                    return field;
                }),
            }));
            setPurposes(purposeDetails?.data?.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const onSubmit = async (data) => {
        if (serverLoading) return;
        setServerLoading(true);
        const visitdate = dateFormat(data["date"], "YYYY-MM-DD HH:mm:ss");
        let addVisitDetails = {
            patient_id: patientId,
            visit_date: visitdate,
            purpose: data?.purpose,
            primary_visit_id: data?.primary_visit_id,
            examined_by: data?.examined_by,
        };
        await commonPostApi(API_ENDPOINTS.ADD_PATIENT_VISIT_DETAILS, addVisitDetails).then(
            (data) => {
                if (data?.statusText === "Created") {
                    reset();
                    showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.VISIT.ADDED);
                    props?.setVisible?.(false);
                } else if (data?.status === 400) {
                    showToast(TOAST_TYPE.ERROR, data?.response?.data?.message);
                } else {
                    showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
                }
                setServerLoading(false);
            }
        );
    };

    const addVisitsButton = [
        {
            type: "primarySuccessButton",
            label: "Submit",
            class: "col-4",
            onClick: handleSubmit(onSubmit),
        },
        {
            type: "primaryCancelButton",
            label: "Discard",
            class: "col-4",
            onClick: () => {
                props?.setVisible?.(false);
            },
        },
    ];

    useEffect(() => {
        init();
        setValue("date", new Date());
    }, []);

    useEffect(() => {
        setVisible(props?.visible);
    }, [props?.visible]);

    useEffect(() => {
        const label = purposes?.find(option => option.lookup_id === selectedPurpose)?.name;
        const initialVisit = purposes?.find(option => option.name === "Initial Visit")?.lookup_id;
        const hasField = renderInput?.formRender?.some(field => field.name === "primary_visit_id");

        if (label && label !== "Initial Visit") {
            if (!hasField) {
                const primary_visit_id = {
                    type: "dropdown",
                    name: "primary_visit_id",
                    label: "Primary Visit Id",
                    fieldLabel: "primary_visit_id",
                    options: patientVisitAllDetails?.filter(visit => visit?.purpose === initialVisit)?.map(visit => ({
                        label: visit?.patient_visit_code,
                        value: visit?.patient_visit_id,
                    })) || [],
                    rules: {
                        required: "Primary visit id is required",
                    },
                };

                setRenderInput((prev) => {
                    const formRender = prev?.formRender || [];
                    const purposeIndex = formRender.findIndex(field => field.name === "purpose");
                    const newFormRender = [...formRender];

                    // Insert after purpose
                    newFormRender.splice(purposeIndex + 1, 0, primary_visit_id);

                    return {
                        ...prev,
                        formRender: newFormRender,
                    };
                });
            }
        } else {
            if (hasField) {
                setRenderInput((prev) => ({
                    ...prev,
                    formRender: prev?.formRender?.filter(field => field.name !== "primary_visit_id"),
                }));
                setValue("primary_visit_id", ""); // Clear value only when removing
            }
        }
    }, [selectedPurpose, purposes, patientVisitAllDetails]);

    return (
        <React.Fragment>
            <PrimarySideBar
                visible={visible}
                onHide={() => props?.setVisible?.(false)}
                header={renderInput?.header}
            >
                {renderInput?.buttons ? (
                    <React.Fragment>
                        <InputIterator
                            control={control}
                            errors={errors}
                            formRender={renderInput?.formRender}
                        />
                        <div className="pt-3">
                            <AddButtonSection disabled={serverLoading} buttons={renderInput?.buttons} />
                        </div>
                    </React.Fragment>
                ) : (
                    <Loader />
                )}
            </PrimarySideBar>
            <DevTool control={control} />
        </React.Fragment>
    );
}
